const percentToNumber = percent => parseInt(percent, 10) / 100;
const makeNumber = value =>
  typeof value === 'string' && value[value.length - 1] === '%'
    ? percentToNumber(value)
    : Number(value);
const normalizeHue = h => (h > 1 ? h / 360 : h);

export function makeRGBA(color, opacity) {
  const colorRGBA = colorToRGBA(color);
  colorRGBA[3] =
    typeof opacity === 'function' ? opacity(colorRGBA[3]) : opacity;
  return `rgba(${colorRGBA.join(',')})`;
}

function colorToRGBA(color) {
  let rgba;

  if (color.indexOf('hsl') === 0) {
    const [, h, s, l, a = 1] = color.match(
      /^hsla?\((.+?),(.+?),(.+?)(?:,(.+?))?\)$/i,
    );
    const rgb = hslToRgb(normalizeHue(h), makeNumber(s), makeNumber(l));
    return [...rgb, a];
  }

  if (color.charAt(0) === '#') {
    color = color.slice(1);
  }

  if (color.length === 6) {
    rgba = color.match(/../g).map(function(pair) {
      return parseInt(pair, 16);
    });
    rgba.push(1);
    return rgba;
  }

  if (color.length === 3) {
    rgba = color.match(/./g).map(function(pair) {
      return parseInt(`${pair}${pair}`, 16);
    });
    rgba.push(1);
    return rgba;
  }

  rgba = color
    .replace(/^rgba?\(/, '')
    .replace(/\)$/, '')
    .split(',')
    .map(value => Number(value));

  if (rgba.length === 3) {
    rgba.push(1);
  }

  return rgba;
}

function hue2rgb(p: number, q: number, t: number) {
  if (t < 0) {
    t += 1;
  }
  if (t > 1) {
    t -= 1;
  }
  if (t < 1 / 6) {
    return p + (q - p) * 6 * t;
  }
  if (t < 1 / 2) {
    return q;
  }
  if (t < 2 / 3) {
    return p + (q - p) * (2 / 3 - t) * 6;
  }
  return p;
}

/**
 * Converts an HSL color value to RGB. Conversion formula
 * adapted from http://en.wikipedia.org/wiki/HSL_color_space.
 * Assumes h, s, and l are contained in the set [0, 1] and
 * returns r, g, and b in the set [0, 255].
 *
 * @param   {number}  h       The hue
 * @param   {number}  s       The saturation
 * @param   {number}  l       The lightness
 * @return  {Array}           The RGB representation
 */
export function hslToRgb(h: number, s: number, l: number) {
  let r, g, b;

  if (s === 0) {
    r = g = b = l; // achromatic
  } else {
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r = hue2rgb(p, q, h + 1 / 3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1 / 3);
  }

  return [Math.round(r * 255), Math.round(g * 255), Math.round(b * 255)];
}
